<template>
  <v-card outlined class="shift-comparison mb-5">
    <v-card-title class="comparison-title px-4 py-3">
      <div class="d-flex align-center min-width-0">
        <v-avatar size="36" color="blue-grey darken-3" class="mr-3">
          <v-icon color="blue lighten-2" small>mdi-calendar-clock</v-icon>
        </v-avatar>
        <div class="min-width-0">
          <div class="subtitle-1 font-weight-bold text-truncate">Schicht-Abgleich</div>
          <div class="caption grey--text text--lighten-1">Alle Center · reine Ansicht · keine Daten werden verändert</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn :color="opened ? 'grey darken-2' : 'blue darken-1'" small depressed @click="toggleComparison">
        <v-icon left small>{{ opened ? "mdi-chevron-up" : "mdi-eye-outline" }}</v-icon>
        {{ opened ? "Ausblenden" : "Anzeigen" }}
      </v-btn>
    </v-card-title>

    <v-expand-transition>
      <div v-if="opened">
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row dense align="center" class="mb-2">
            <v-col cols="12" sm="4" md="3">
              <v-text-field
                v-model.number="threshold"
                type="number"
                min="0"
                max="180"
                step="1"
                dense
                outlined
                hide-details
                suffix="Min."
                label="Toleranz ab"
                prepend-inner-icon="mdi-tune-variant"
                @change="persistThreshold"></v-text-field>
            </v-col>
            <v-col cols="12" sm="8" md="9" class="d-flex align-center flex-wrap">
              <v-btn-toggle v-model="filter" mandatory dense class="comparison-filter mr-3 mb-1">
                <v-btn value="findings" small> Auffällig </v-btn>
                <v-btn value="all" small> Alle </v-btn>
              </v-btn-toggle>
              <v-chip v-if="comparison" small color="green darken-2" class="mr-2 mb-1">
                <v-icon left x-small>mdi-check</v-icon>{{ summary.ok }} passend
              </v-chip>
              <v-chip v-if="comparison" small color="red darken-2" class="mr-2 mb-1">
                <v-icon left x-small>mdi-alert</v-icon>{{ summary.deviation }} abweichend
              </v-chip>
              <v-chip v-if="comparison" small color="orange darken-2" class="mr-2 mb-1">
                <v-icon left x-small>mdi-information</v-icon>{{ summary.context }} mit Kontext
              </v-chip>
              <v-chip v-if="comparison" small color="grey darken-2" class="mb-1">
                <v-icon left x-small>mdi-clock-outline</v-icon>{{ summary.neutral }} offen
              </v-chip>
            </v-col>
          </v-row>

          <div
            v-if="comparison && comparison.employee && comparison.employee.studios && comparison.employee.studios.length"
            class="d-flex align-center flex-wrap mb-3">
            <span class="caption grey--text mr-2">Eingeblendete Center:</span>
            <v-chip
              v-for="studioItem in comparison.employee.studios"
              :key="studioItem.id"
              x-small
              outlined
              color="blue-grey lighten-2"
              class="mr-2 mb-1">
              <v-icon left x-small>mdi-domain</v-icon>{{ studioItem.name }}
            </v-chip>
          </div>

          <v-alert dense text type="info" class="caption mb-3">
            Markiert wird ab einschließlich {{ normalizedThreshold }} Minuten. Kalenderpunkte werden nur im jeweiligen Abweichungszeitraum angezeigt
            und sind keine automatische Begründung. Das bei der Buchung gespeicherte Center wird nicht bewertet.
          </v-alert>

          <v-alert
            v-if="comparison && comparison.meta && comparison.meta.includesLegacyAttendanceWithoutStudio"
            dense
            text
            type="warning"
            class="caption">
            Einzelne ältere Buchungen haben keine Center-Zuordnung. Sie werden einmalig als „Center unbekannt“ im Gesamtvergleich berücksichtigt.
          </v-alert>

          <div v-if="loading" class="py-8 text-center">
            <v-progress-circular indeterminate color="blue lighten-1"></v-progress-circular>
            <div class="caption grey--text mt-3">Schichten und Stempelzeiten werden verglichen …</div>
          </div>

          <v-alert v-else-if="error" type="error" text dense>
            {{ error }}
          </v-alert>

          <v-alert v-else-if="comparison && visibleDays.length === 0" type="success" text dense>
            {{
              filter === "findings"
                ? "Für diese Toleranz wurden keine Auffälligkeiten gefunden."
                : "Für diesen Zeitraum gibt es keine vergleichbaren Einträge."
            }}
          </v-alert>

          <div v-else-if="comparison">
            <section v-for="day in visibleDays" :key="day.date" class="comparison-day mb-4">
              <div class="day-heading px-3 py-2">
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="blue lighten-2">mdi-calendar</v-icon>
                  <strong>{{ formatDay(day.date) }}</strong>
                </div>
              </div>

              <v-expansion-panels flat accordion class="comparison-rows">
                <v-expansion-panel v-for="row in day.rows" :key="row.id" class="comparison-row">
                  <v-expansion-panel-header class="px-3 py-2">
                    <div class="row-summary">
                      <div class="status-cell">
                        <v-avatar :color="statusColor(row.displayState)" size="30">
                          <v-icon x-small color="white">{{ statusIcon(row.displayState) }}</v-icon>
                        </v-avatar>
                      </div>
                      <div class="time-cell">
                        <span class="cell-label">Soll</span>
                        <strong>{{ plannedLabel(row) }}</strong>
                        <small
                          >{{ rowTitle(row) }}<span v-if="rowStudioLabel(row)"> · {{ rowStudioLabel(row) }}</span></small
                        >
                      </div>
                      <div class="time-cell">
                        <span class="cell-label">Ist</span>
                        <strong>{{ actualLabel(row) }}</strong>
                        <small v-if="row.actual && row.actual.intervals && row.actual.intervals.length > 1">
                          {{ row.actual.intervals.length }} Arbeitsintervalle
                        </small>
                      </div>
                      <div class="delta-cell">
                        <span class="cell-label">Abweichung</span>
                        <strong>{{ compactDelta(row) }}</strong>
                        <small>{{ statusLabel(row.displayState) }}</small>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-divider class="mb-3"></v-divider>
                    <v-row dense>
                      <v-col cols="12" md="5">
                        <div class="detail-label">Bewertung</div>
                        <div v-if="detailMessages(row).length">
                          <div v-for="(message, index) in detailMessages(row)" :key="row.id + '-message-' + index" class="detail-line">
                            <v-icon x-small :color="statusColor(row.displayState)" class="mr-2">mdi-circle-medium</v-icon>{{ message }}
                          </div>
                        </div>
                        <div v-else class="detail-line grey--text">Keine relevante Abweichung.</div>
                      </v-col>

                      <v-col cols="12" md="3">
                        <div class="detail-label">Stempelintervalle</div>
                        <div v-if="row.actual && row.actual.intervals && row.actual.intervals.length">
                          <div v-for="interval in row.actual.intervals" :key="interval.id" class="detail-line">
                            {{ formatDateTime(interval.start, day.date) }} – {{ interval.end ? formatDateTime(interval.end, day.date) : "offen" }}
                          </div>
                        </div>
                        <div v-else class="detail-line grey--text">Keine Buchung</div>
                      </v-col>

                      <v-col cols="12" md="4">
                        <div class="detail-label">Kalender im Abweichungszeitraum</div>
                        <div v-if="row.relevantContext && row.relevantContext.length">
                          <div v-for="context in row.relevantContext" :key="context.id + '-detail'" class="detail-line context-line">
                            <v-icon x-small :color="contextColor(context)" class="mr-2">{{ contextIcon(context) }}</v-icon>
                            <span>
                              <strong>{{ contextLabel(context) }}</strong>
                              <span v-if="context.note" class="grey--text text--lighten-1"> · {{ context.note }}</span>
                            </span>
                          </div>
                        </div>
                        <div v-else class="detail-line grey--text">Kein passender Kalendereintrag in dieser Zeit</div>
                      </v-col>
                    </v-row>

                    <v-alert v-if="hasCorrectionPreview(row)" dense text color="blue" class="mt-3 mb-0 correction-preview">
                      <div class="font-weight-bold caption mb-1">
                        <v-icon x-small color="blue lighten-2" class="mr-1">mdi-file-clock-outline</v-icon>Offene Korrektur – nur Vorschau
                      </div>
                      <div class="caption">
                        Beantragtes Ist: {{ previewActualLabel(row) }}
                        <span v-if="previewDeltaLabel(row)"> · {{ previewDeltaLabel(row) }}</span>
                      </div>
                    </v-alert>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </section>
          </div>
        </v-card-text>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { contextsForRow } from "./shiftAttendanceContext"

export default {
  name: "ShiftAttendanceComparison",
  props: {
    employeeId: { required: true },
    employeeName: { type: String, default: "" },
    studio: { required: true },
    dateRange: { type: Array, default: () => [] },
    month: { required: true },
    company: { type: String, default: null },
  },
  data() {
    return {
      opened: false,
      loading: false,
      error: "",
      comparison: null,
      threshold: 10,
      filter: "findings",
    }
  },
  computed: {
    normalizedThreshold() {
      const value = Number(this.threshold)
      if (!Number.isFinite(value)) return 10
      return Math.max(0, Math.min(180, Math.round(value)))
    },
    storageKey() {
      return `timetracker.shiftComparisonThreshold.${userService.getUserId() || "anonymous"}`
    },
    resolvedRange() {
      const hasSelectedStart = Boolean(this.dateRange && this.dateRange[0])
      const start = hasSelectedStart ? this.$moment(this.dateRange[0]) : this.$moment(this.month).startOf("month")
      const end =
        this.dateRange && this.dateRange[1]
          ? this.$moment(this.dateRange[1])
          : hasSelectedStart
          ? this.$moment(this.dateRange[0])
          : this.$moment(this.month).endOf("month")
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") }
    },
    comparisonKey() {
      const studioId = this.studio && this.studio._id ? this.studio._id : this.studio
      return [this.employeeId, studioId, this.resolvedRange.start, this.resolvedRange.end, this.company || ""].join("|")
    },
    evaluatedDays() {
      if (!this.comparison || !Array.isArray(this.comparison.days)) return []
      return this.comparison.days.map((day) => {
        const sourceRows = day.rows || []
        return {
          ...day,
          rows: sourceRows.map((row) => {
            const relevantContext = contextsForRow(row, day.context || [], sourceRows, this.normalizedThreshold)
            const evaluatedRow = { ...row, relevantContext }
            return { ...evaluatedRow, displayState: this.evaluateRow(evaluatedRow) }
          }),
        }
      })
    },
    visibleDays() {
      return this.evaluatedDays
        .map((day) => ({
          ...day,
          rows: this.filter === "all" ? day.rows : day.rows.filter((row) => row.displayState === "deviation" || row.displayState === "context"),
        }))
        .filter((day) => day.rows.length > 0)
    },
    summary() {
      const summary = { ok: 0, deviation: 0, context: 0, neutral: 0 }
      this.evaluatedDays.forEach((day) => {
        day.rows.forEach((row) => {
          if (row.displayState === "ok") summary.ok++
          else if (row.displayState === "deviation") summary.deviation++
          else if (row.displayState === "context") summary.context++
          else summary.neutral++
        })
      })
      return summary
    },
  },
  watch: {
    comparisonKey() {
      this.comparison = null
      this.error = ""
      if (this.opened) this.fetchComparison()
    },
  },
  created() {
    const stored = Number(localStorage.getItem(this.storageKey))
    if (Number.isFinite(stored) && stored >= 0 && stored <= 180) this.threshold = Math.round(stored)
  },
  methods: {
    toggleComparison() {
      this.opened = !this.opened
      if (this.opened && !this.comparison && !this.loading) this.fetchComparison()
    },
    async fetchComparison() {
      if (!this.employeeId || !this.studio) return
      this.loading = true
      this.error = ""
      try {
        this.comparison = await api.fetchShiftAttendanceComparison({
          employeeId: this.employeeId,
          startDate: this.resolvedRange.start,
          endDate: this.resolvedRange.end,
          studio: this.studio,
          company: this.company,
        })
      } catch (error) {
        this.comparison = null
        this.error = error?.response?.data?.message || "Der Schicht-Abgleich konnte nicht geladen werden."
      } finally {
        this.loading = false
      }
    },
    persistThreshold() {
      this.threshold = this.normalizedThreshold
      localStorage.setItem(this.storageKey, String(this.normalizedThreshold))
    },
    hasStructuralIssue(row) {
      return ["missing", "unplanned", "invalid"].includes(row.state) || Boolean(row.issues && row.issues.length)
    },
    isMarkedMinuteDelta(value) {
      return Number.isFinite(value) && value !== 0 && Math.abs(value) >= this.normalizedThreshold
    },
    hasMinuteDeviation(row) {
      const deltas = row.deltas || {}
      return [deltas.start, deltas.end].some((value) => this.isMarkedMinuteDelta(value))
    },
    evaluateRow(row) {
      if (row.state === "explained") return "context"
      if (["ongoing", "future"].includes(row.state)) return "neutral"
      if (this.hasStructuralIssue(row) || this.hasMinuteDeviation(row))
        return row.relevantContext && row.relevantContext.length ? "context" : "deviation"
      return "ok"
    },
    statusColor(state) {
      return { ok: "green darken-2", deviation: "red darken-2", context: "orange darken-2", neutral: "grey darken-1" }[state] || "grey"
    },
    statusIcon(state) {
      return { ok: "mdi-check", deviation: "mdi-alert", context: "mdi-information", neutral: "mdi-clock-outline" }[state] || "mdi-help"
    },
    statusLabel(state) {
      return { ok: "passt", deviation: "Abweichung", context: "Kontext vorhanden", neutral: "nicht abschließend" }[state] || "offen"
    },
    formatDay(date) {
      return this.$moment(date, "YYYY-MM-DD").format("dddd, DD.MM.YYYY")
    },
    formatDateTime(value, rowDate) {
      if (!value) return "—"
      return value.date && value.date !== rowDate ? `${this.$moment(value.date).format("DD.MM.")} ${value.time}` : value.time
    },
    plannedLabel(row) {
      if (!row.planned) return "Keine Schicht"
      return `${this.formatDateTime(row.planned.start, row.date)} – ${this.formatDateTime(row.planned.end, row.date)}`
    },
    actualLabel(row) {
      if (!row.actual) return "Keine Buchung"
      const start = this.formatDateTime(row.actual.start, row.date)
      const end = row.actual.end ? this.formatDateTime(row.actual.end, row.date) : "offen"
      return `${start} – ${end}`
    },
    rowTitle(row) {
      if (row.state === "explained" && row.explanation && row.explanation.labels) return row.explanation.labels.join(", ")
      if (row.planned && row.planned.labels && row.planned.labels.length) return row.planned.labels.join(", ")
      return row.kind === "unplanned" ? "Ohne Schicht" : "Buchungsfolge"
    },
    rowStudioLabel(row) {
      const studio = row.planned?.studio
      return studio?.name || (studio?.id ? "Center" : "")
    },
    formatDelta(value, prefix) {
      if (!Number.isFinite(value)) return null
      const sign = value > 0 ? "+" : ""
      return `${prefix} ${sign}${value} Min.`
    },
    compactDelta(row) {
      if (row.issues && row.issues.length) return row.issues[0].label
      const values = [this.formatDelta(row.deltas?.start, "Start"), this.formatDelta(row.deltas?.end, "Ende")].filter(Boolean)
      if (values.length) return values.join(" · ")
      if (row.state === "explained") return "abgedeckt"
      if (row.state === "ongoing" || row.state === "future") return "laufend"
      return "—"
    },
    detailMessages(row) {
      const messages = (row.issues || []).map((issue) => issue.label)
      const start = row.deltas?.start
      const end = row.deltas?.end
      if (Number.isFinite(start)) {
        if (this.isMarkedMinuteDelta(start) && start < 0) messages.push(`${Math.abs(start)} Minuten zu früh eingecheckt`)
        else if (this.isMarkedMinuteDelta(start) && start > 0) messages.push(`${start} Minuten zu spät eingecheckt`)
        else messages.push(`Check-in innerhalb der Toleranz (${start > 0 ? "+" : ""}${start} Min.)`)
      }
      if (Number.isFinite(end)) {
        if (this.isMarkedMinuteDelta(end) && end < 0) messages.push(`${Math.abs(end)} Minuten zu früh ausgecheckt`)
        else if (this.isMarkedMinuteDelta(end) && end > 0) messages.push(`${end} Minuten zu spät ausgecheckt`)
        else messages.push(`Check-out innerhalb der Toleranz (${end > 0 ? "+" : ""}${end} Min.)`)
      }
      if (row.state === "explained") messages.push(`Schichtbereich durch ${this.rowTitle(row)} abgedeckt`)
      if (row.state === "ongoing" || row.state === "future") messages.push("Dieser Schichtbereich ist noch nicht abschließend bewertbar")
      return messages
    },
    contextIcon(context) {
      return (
        {
          appointment: "mdi-calendar-clock",
          task: "mdi-checkbox-marked-circle-outline",
          sick: "mdi-medical-bag",
          holiday: "mdi-palm-tree",
          freewish: "mdi-calendar-blank",
          daily_note: "mdi-note-text-outline",
          shift_note: "mdi-clipboard-text-outline",
          correction: "mdi-file-clock-outline",
        }[context.kind] || "mdi-information-outline"
      )
    },
    contextColor(context) {
      return (
        {
          appointment: "blue lighten-2",
          task: "amber",
          sick: "orange",
          holiday: "teal lighten-1",
          freewish: "cyan",
          daily_note: "purple lighten-2",
          shift_note: "blue-grey lighten-2",
          correction: "blue lighten-1",
        }[context.kind] || "grey lighten-1"
      )
    },
    contextLabel(context) {
      const time = context.start ? `${context.start}${context.end ? `–${context.end}` : ""}` : ""
      const label = time ? `${time} ${context.label}` : context.label
      return context.studio?.name ? `${label} · ${context.studio.name}` : label
    },
    hasCorrectionPreview(row) {
      if (!row.proposed) return false
      return JSON.stringify({ state: row.state, actual: row.actual, deltas: row.deltas, issues: row.issues }) !== JSON.stringify(row.proposed)
    },
    previewActualLabel(row) {
      if (!row.proposed || !row.proposed.actual) return "keine Buchung"
      const start = this.formatDateTime(row.proposed.actual.start, row.date)
      const end = row.proposed.actual.end ? this.formatDateTime(row.proposed.actual.end, row.date) : "offen"
      return `${start} – ${end}`
    },
    previewDeltaLabel(row) {
      if (!row.proposed || !row.proposed.deltas) return ""
      return [this.formatDelta(row.proposed.deltas.start, "Start"), this.formatDelta(row.proposed.deltas.end, "Ende")].filter(Boolean).join(" · ")
    },
  },
}
</script>

<style lang="scss" scoped>
.shift-comparison {
  background: rgba(21, 31, 43, 0.78) !important;
  border-color: rgba(100, 181, 246, 0.2) !important;
}

.comparison-title {
  min-height: 62px;
}

.min-width-0 {
  min-width: 0;
}

.comparison-filter {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.comparison-day {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 10px;
}

.day-heading {
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.comparison-row {
  background: rgba(13, 21, 30, 0.55) !important;
}

.comparison-row + .comparison-row {
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.row-summary {
  display: grid;
  grid-template-columns: 42px minmax(150px, 1fr) minmax(150px, 1fr) minmax(190px, 1.1fr);
  gap: 12px;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.time-cell,
.delta-cell {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.time-cell strong,
.delta-cell strong,
.time-cell small,
.delta-cell small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-cell small,
.delta-cell small,
.cell-label {
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.7rem;
}

.detail-label {
  margin-bottom: 7px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 5px;
  font-size: 0.82rem;
  line-height: 1.35;
}

.context-line span {
  min-width: 0;
}

.correction-preview {
  border: 1px solid rgba(100, 181, 246, 0.25) !important;
}

@media (max-width: 800px) {
  .comparison-title {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }

  .comparison-title .v-spacer {
    display: none;
  }

  .row-summary {
    grid-template-columns: 34px 1fr;
    gap: 8px 10px;
  }

  .status-cell {
    grid-row: 1 / span 3;
  }

  .time-cell,
  .delta-cell {
    grid-column: 2;
  }

  .time-cell strong,
  .delta-cell strong,
  .time-cell small,
  .delta-cell small {
    white-space: normal;
  }
}
</style>
